import React, { useMemo, useState } from 'react'
import {
  Breadcrumbs,
  BREADCRUMB_SHOWCASE_PRESETS,
  buildBreadcrumbPresetItems,
  type BreadcrumbShowcasePreset,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './BreadcrumbsShowcase.css'

const PRESET_LABELS: Record<BreadcrumbShowcasePreset, string> = {
  default: 'Default',
  multiple: 'Multiple breadcrumbs',
  long: 'Long breadcrumbs',
}

const formatPresetLabel = (preset: BreadcrumbShowcasePreset): string =>
  PRESET_LABELS[preset]

const BreadcrumbsShowcase: React.FC = () => {
  const [highlightedPreset, setHighlightedPreset] =
    useState<BreadcrumbShowcasePreset>('default')
  const [showBackIcon, setShowBackIcon] = useState(true)
  const [maxItems, setMaxItems] = useState(3)
  const [darkMode, setDarkMode] = useState(false)

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Behavior',
        fields: [
          {
            id: 'breadcrumbs-preset',
            label: 'Highlighted preset',
            control: (
              <select
                id="breadcrumbs-preset"
                className="ui-field-control"
                value={highlightedPreset}
                onChange={(event) =>
                  setHighlightedPreset(
                    event.target.value as BreadcrumbShowcasePreset,
                  )
                }
              >
                {BREADCRUMB_SHOWCASE_PRESETS.map((preset) => (
                  <option key={preset} value={preset}>
                    {formatPresetLabel(preset)}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'breadcrumbs-back-icon',
            label: 'Back icon',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="breadcrumbs-back-icon"
                  type="checkbox"
                  checked={showBackIcon}
                  onChange={(event) => setShowBackIcon(event.target.checked)}
                />
                <span>Visible</span>
              </label>
            ),
          },
          {
            id: 'breadcrumbs-max-items',
            label: 'Max items before collapse',
            control: (
              <input
                id="breadcrumbs-max-items"
                className="ui-field-control"
                type="number"
                min={2}
                max={6}
                value={maxItems}
                onChange={(event) =>
                  setMaxItems(Number(event.target.value) || 3)
                }
              />
            ),
          },
          {
            id: 'breadcrumbs-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="breadcrumbs-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, highlightedPreset, maxItems, showBackIcon],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Breadcrumbs"
        description="Navigation trail with back icon, link segments, and collapsed long paths."
        stageClassName="ui-preview__stage--wide breadcrumbs-showcase__stage-wrap"
      >
        <div
          className={`breadcrumbs-showcase__sheet${
            darkMode ? ' breadcrumbs-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Breadcrumbs with the highlighted preset and panel options.
            </p>
            <Breadcrumbs
              items={buildBreadcrumbPresetItems(highlightedPreset)}
              showBackIcon={showBackIcon}
              maxItems={highlightedPreset === 'long' ? maxItems : undefined}
              darkMode={darkMode}
            />
          </section>

          {BREADCRUMB_SHOWCASE_PRESETS.map((preset) => (
            <div
              key={preset}
              className={`breadcrumbs-showcase__row${
                preset === highlightedPreset
                  ? ' breadcrumbs-showcase__row--selected'
                  : ''
              }${darkMode ? ' breadcrumbs-showcase__row--dark' : ''}`}
            >
              <span className="breadcrumbs-showcase__row-label">
                {formatPresetLabel(preset)}
              </span>
              <div className="breadcrumbs-showcase__preview">
                <Breadcrumbs
                  items={buildBreadcrumbPresetItems(preset)}
                  showBackIcon={showBackIcon}
                  maxItems={preset === 'long' ? maxItems : undefined}
                  darkMode={darkMode}
                />
              </div>
            </div>
          ))}
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Breadcrumbs"
        sections={propertySections}
      />
    </>
  )
}

export default BreadcrumbsShowcase
